import {
  Button,
  ButtonGroup,
  ConfirmationModal,
  ModalBody,
  Paragraph,
  SmallModal,
} from "@george-labs.com/design-system"
import dayjs from "../../utils/dayjs"
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { useOfferData } from "../../app/hooks"

const OfferModalContext = createContext(null)

export const useSaveModal = () => useContext(OfferModalContext)

interface IOfferModalProviderProps {
  children: ReactNode
}

export const OfferModalProvider: React.FC<IOfferModalProviderProps> = ({
  children,
}) => {
  const { t } = useTranslation()
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false)
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)
  const [onConfirm, setOnConfirm] = useState(() => () => {})
  const offerData = useOfferData()
  const [deadLineInHours, setDeadLineInHours] = useState<number>(undefined)
  const [deadLineInMinutes, setDeadLineInMinutes] = useState<number>(undefined)

  useEffect(() => {
    if (!offerData) {
      return
    }

    const deadLine =
      offerData.offerState === "SOFT_FACTS"
        ? offerData.customerDataRecordingDeadline
        : offerData.claimSubmissionDeadline

    if (!deadLine) {
      return
    }

    const deadLineDate = dayjs(deadLine)
    const now = dayjs()

    const diffInMinutes = deadLineDate?.diff(now, "minutes")
    if (diffInMinutes < 60) {
      setDeadLineInMinutes(diffInMinutes)
      setDeadLineInHours(undefined)
      return
    }

    const diffInHours = deadLineDate?.diff(now, "hours")
    setDeadLineInMinutes(undefined)
    setDeadLineInHours(diffInHours)
  }, [offerData])

  const openSaveModal = useCallback((confirmHandler: () => {}) => {
    setOnConfirm(() => confirmHandler)
    setIsSaveModalOpen(true)
  }, [])

  const openErrorModal = useCallback((confirmHandler: () => {}) => {
    setOnConfirm(() => confirmHandler)
    setIsErrorModalOpen(true)
  }, [])

  const contextValue = useMemo(
    () => ({ openSaveModal, openErrorModal }),
    [openSaveModal, openErrorModal],
  )

  const handleOk = () => {
    if (onConfirm) {
      onConfirm()
    }
    setIsSaveModalOpen(false)
    setIsErrorModalOpen(false)
  }

  const handleCancel = () => {
    setIsSaveModalOpen(false)
  }

  return (
    <OfferModalContext.Provider value={contextValue}>
      {children}

      <SmallModal
        footer={
          <ButtonGroup>
            <Button variant={Button.VARIANT.SECONDARY} onClick={handleCancel}>
              {t("offers.saveModal.cancel")}
            </Button>
            <Button onClick={handleOk}>{t("offers.saveModal.ok")}</Button>
          </ButtonGroup>
        }
        isOpen={isSaveModalOpen}
        title={t("offers.saveModal.title")}
        variant={SmallModal.VARIANT.DEFAULT}
        onClose={handleCancel}
      >
        <ModalBody>
          {deadLineInMinutes !== undefined &&
            t("offers.saveModal.minutes.text", { mins: deadLineInMinutes })}
          {deadLineInHours !== undefined &&
            t("offers.saveModal.hours.text", { hours: deadLineInHours })}
        </ModalBody>
      </SmallModal>

      <ConfirmationModal
        onClose={handleOk}
        animate={true}
        isOpen={isErrorModalOpen}
        closeLabel={t("common.close")}
        confirmationType={ConfirmationModal.CONFIRMATION_TYPE.ERROR}
        title={t("common.errors.backend.title")}
        footer={
          <ButtonGroup>
            <Button onClick={handleOk}>{t("common.button.ok")}</Button>
          </ButtonGroup>
        }
      >
        <ModalBody>
          <Paragraph>{t("common.errors.backend.message")}</Paragraph>
        </ModalBody>
      </ConfirmationModal>
    </OfferModalContext.Provider>
  )
}
